import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import moment from 'moment'
import { pdf } from '@react-pdf/renderer'
import {
  Alert,
  Button,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import SearchIcon from '@mui/icons-material/Search'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import API from '../../../services/API'
import { CRMContext } from '../../CRM'
import PageHeader from '../../kit/PageHeader'
import StatTile from '../../kit/StatTile'
import dataGridSx from '../../kit/dataGridSx'
import { SoftChip } from '../../kit/Chips'
import { formatDate, fromNow } from '../../kit/helpers'
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined'
import HazardLogPDF from './HazardLogPDF'
import CustomerHazardPackPDF from './CustomerHazardPackPDF'
import {
  HAZARD_TYPE_COLOR,
  MODULE_COLOR,
  REGISTERS,
  STATUS_COLOR,
  implementedControls,
  inCustomerPack,
  isHighRisk,
  outstandingControls,
  riskTone,
} from './constants'

const FILTERS = [
  { key: 'all', predicate: () => true },
  {
    key: 'open',
    predicate: (row) => row.status === 'Open' || row.status === 'In Progress',
  },
  { key: 'high', predicate: (row) => isHighRisk(row.initialRisk?.riskRating) },
  { key: 'outstanding', predicate: (row) => outstandingControls(row) > 0 },
  { key: 'customer', predicate: (row) => inCustomerPack(row) },
]

// One list page for both registers. `register` picks which hazards are
// fetched and how the page is titled; everything else is shared.
const Hazards = ({ register = 'Clinical' }) => {
  const meta = REGISTERS[register]
  const other = REGISTERS[register === 'Clinical' ? 'Codebase' : 'Clinical']

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [downloadingPack, setDownloadingPack] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useContext(CRMContext)

  const filterKey = searchParams.get('filter') || 'all'
  const filter = FILTERS.find((f) => f.key === filterKey) ?? FILTERS[0]
  const setFilter = (key) => {
    const next = new URLSearchParams(searchParams)
    if (key === 'all' || key === filterKey) next.delete('filter')
    else next.set('filter', key)
    setSearchParams(next, { replace: true })
  }

  useEffect(() => {
    setLoading(true)
    setError('')
    setRows([])
    API.get(`/admincrm/clinical-risk/hazard-log?register=${register}`)
      .then((res) => {
        if (res.status === 200) setRows(res.data || [])
        else setError(res.data?.message || 'Could not load the hazard log.')
      })
      .catch(() => setError('Could not load the hazard log.'))
      .finally(() => setLoading(false))
  }, [register])

  // Whole register, not the filtered view, so the PDF is a complete log for
  // the safety case. Rendered client-side from the rows already loaded.
  const downloadPdf = async () => {
    setDownloading(true)
    setError('')
    try {
      const downloadedBy = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
      const blob = await pdf(
        <HazardLogPDF register={register} hazards={rows} downloadedBy={downloadedBy} />,
      ).toBlob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `AmbuNet_${meta.short}_Hazard_Log_${moment().format('YYYY-MM-DD')}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error(e)
      setError('Could not generate the PDF.')
    } finally {
      setDownloading(false)
    }
  }

  // The customer pack spans both registers: a Codebase hazard can need a
  // customer setting just as a Clinical one can. Fetch everything, keep only
  // what the CSO has marked customer-facing.
  const downloadCustomerPack = async () => {
    setDownloadingPack(true)
    setError('')
    try {
      const res = await API.get('/admincrm/clinical-risk/hazard-log')
      if (res.status !== 200) throw new Error('load failed')
      const included = (res.data || []).filter(inCustomerPack)
      if (!included.length) {
        setError(
          'No hazards are marked for the customer pack yet. Open a hazard and switch on "Customer hazard pack".',
        )
        return
      }
      const downloadedBy = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
      const blob = await pdf(
        <CustomerHazardPackPDF hazards={included} downloadedBy={downloadedBy} />,
      ).toBlob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `AmbuNet_Customer_Hazard_Pack_${moment().format('YYYY-MM-DD')}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error(e)
      setError('Could not generate the customer pack.')
    } finally {
      setDownloadingPack(false)
    }
  }

  const stats = useMemo(
    () => ({
      total: rows.length,
      open: rows.filter(FILTERS[1].predicate).length,
      high: rows.filter(FILTERS[2].predicate).length,
      outstanding: rows.filter(FILTERS[3].predicate).length,
      customer: rows.filter(FILTERS[4].predicate).length,
    }),
    [rows],
  )

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase()
    return rows.filter(filter.predicate).filter((row) => {
      if (!query) return true
      return [
        row.hazardID,
        row.title,
        row.module,
        row.hazardType,
        row.status,
        row.source,
        row.initialRisk?.riskRating,
        row.residualRisk?.riskRating,
        row.notes,
        ...(row.linkedFeatures || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query)
    })
  }, [rows, filter, search])

  const columns = [
    {
      field: 'hazardID',
      headerName: 'ID',
      minWidth: 120,
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Typography>
      ),
    },
    {
      field: 'title',
      headerName: 'Hazard',
      minWidth: 320,
      flex: 1.6,
      renderCell: ({ row }) => (
        <Stack minWidth={0} py={0.5}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ whiteSpace: 'normal', lineHeight: 1.3 }}
          >
            {row.title}
          </Typography>
          <Stack direction="row" gap={0.75} alignItems="center" mt={0.5} flexWrap="wrap">
            {row.module && (
              <SoftChip
                color={MODULE_COLOR[row.module] || '#64748B'}
                label={row.module}
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
            {row.hazardType && (
              <Typography
                variant="caption"
                sx={{
                  color: HAZARD_TYPE_COLOR[row.hazardType] || 'text.secondary',
                  fontWeight: 600,
                }}
              >
                {row.hazardType}
              </Typography>
            )}
          </Stack>
        </Stack>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 130,
      flex: 0.5,
      renderCell: ({ value }) => (
        <SoftChip color={STATUS_COLOR[value] || '#64748B'} label={value || 'Unknown'} />
      ),
    },
    {
      field: 'initialRisk',
      headerName: 'Initial',
      minWidth: 110,
      flex: 0.4,
      valueGetter: (value, row) => row?.initialRisk?.riskRating || '',
      renderCell: ({ value, row }) =>
        value ? (
          <Tooltip
            title={[row.initialRisk?.likelihood, row.initialRisk?.severity]
              .filter(Boolean)
              .join(' / ')}
          >
            <span>
              <SoftChip color={riskTone(value)} label={value} />
            </span>
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.disabled">
            Not scored
          </Typography>
        ),
    },
    {
      field: 'residualRisk',
      headerName: 'Residual',
      minWidth: 110,
      flex: 0.4,
      valueGetter: (value, row) => row?.residualRisk?.riskRating || '',
      renderCell: ({ value, row }) =>
        value ? (
          <Tooltip
            title={[row.residualRisk?.likelihood, row.residualRisk?.severity]
              .filter(Boolean)
              .join(' / ')}
          >
            <span>
              <SoftChip color={riskTone(value)} label={value} />
            </span>
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.disabled">
            Not scored
          </Typography>
        ),
    },
    {
      field: 'controls',
      headerName: 'Controls',
      minWidth: 130,
      flex: 0.5,
      valueGetter: (value, row) => implementedControls(row),
      renderCell: ({ row }) => {
        const total = (row.controlMeasures || []).length
        const done = implementedControls(row)
        const outstanding = outstandingControls(row)
        if (!total) {
          return (
            <Typography variant="caption" color="text.disabled">
              None recorded
            </Typography>
          )
        }
        return (
          <Stack minWidth={0}>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {done} of {total} in place
            </Typography>
            {outstanding > 0 && (
              <Typography variant="caption" sx={{ color: '#B54708', fontWeight: 600 }}>
                {outstanding} planned
              </Typography>
            )}
          </Stack>
        )
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      minWidth: 130,
      flex: 0.5,
      valueGetter: (value) => (value ? new Date(value) : null),
      sortComparator: (a, b) => (a?.getTime() ?? 0) - (b?.getTime() ?? 0),
      renderCell: ({ value }) =>
        value ? (
          <Tooltip title={formatDate(value)}>
            <Typography variant="body2" color="text.secondary">
              {fromNow(value)}
            </Typography>
          </Tooltip>
        ) : (
          ''
        ),
    },
  ]

  return (
    <Stack gap={2.5}>
      <PageHeader
        eyebrow="Clinical risk"
        title={meta.label}
        subtitle={meta.subtitle}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<SwapHorizIcon />}
              onClick={() => navigate(other.path)}
            >
              {other.label}
            </Button>
            <Tooltip title="Landscape A4 PDF of the whole register: cover summary, colour-coded register table and per-hazard detail">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={downloadPdf}
                  disabled={downloading || loading || rows.length === 0}
                >
                  {downloading ? 'Building PDF…' : 'Download PDF'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="DCB0160 support pack: only hazards marked customer-facing, from both registers, with the guidance written for deploying organisations. No internal notes or evidence.">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<HandshakeOutlinedIcon />}
                  onClick={downloadCustomerPack}
                  disabled={downloadingPack}
                >
                  {downloadingPack ? 'Building pack…' : 'Customer pack PDF'}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => navigate(`/clinical-risk/new?register=${register}`)}
            >
              New hazard
            </Button>
          </>
        }
      />

      <Grid container spacing={1.5}>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="Logged"
            value={stats.total}
            caption={`Hazards in the ${meta.short.toLowerCase()} register`}
            icon={<FactCheckOutlinedIcon />}
            tone={meta.color}
            active={filterKey === 'all'}
            onClick={() => setFilter('all')}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="Open"
            value={stats.open}
            caption="Open or in progress"
            icon={<WarningAmberIcon />}
            tone="#B54708"
            active={filterKey === 'open'}
            onClick={() => setFilter('open')}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="High or critical"
            value={stats.high}
            caption="By initial risk rating"
            icon={<LocalFireDepartmentOutlinedIcon />}
            tone={stats.high ? '#B42318' : '#64748B'}
            active={filterKey === 'high'}
            onClick={() => setFilter('high')}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="In customer pack"
            value={stats.customer}
            caption="Marked customer-facing for DCB0160"
            icon={<HandshakeOutlinedIcon />}
            tone="#0F6670"
            active={filterKey === 'customer'}
            onClick={() => setFilter('customer')}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="Controls outstanding"
            value={stats.outstanding}
            caption="Hazards with a planned control not yet in place"
            icon={<PendingActionsIcon />}
            tone="#5B48B0"
            active={filterKey === 'outstanding'}
            onClick={() => setFilter('outstanding')}
          />
        </Grid>
      </Grid>

      {error && <Alert severity="error">{error}</Alert>}

      <Stack gap={1.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          gap={1}
        >
          <Typography variant="body2" color="text.secondary">
            {visibleRows.length} of {rows.length} {rows.length === 1 ? 'hazard' : 'hazards'}
            {filterKey !== 'all' ? ' (filtered)' : ''}
          </Typography>
          <TextField
            size="small"
            placeholder="Search ID, title, module, type, notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: { xs: '100%', md: 360 }, bgcolor: '#FFFFFF' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <DataGrid
          rows={visibleRows}
          columns={columns}
          getRowId={(row) => row._id}
          loading={loading}
          autoHeight
          getRowHeight={() => 'auto'}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
            sorting: { sortModel: [{ field: 'hazardID', sort: 'asc' }] },
          }}
          onRowClick={({ row }) => navigate(`/clinical-risk/${row._id}`)}
          localeText={{
            noRowsLabel: loading ? 'Loading…' : 'No hazards match.',
          }}
          sx={{
            ...dataGridSx,
            '& .MuiDataGrid-cell': {
              ...dataGridSx['& .MuiDataGrid-cell'],
              py: 1,
            },
          }}
        />
      </Stack>
    </Stack>
  )
}

export default Hazards
